import {
  DehydratedState,
  Hydrate,
  QueryClientProvider,
} from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import * as React from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { ThemeProvider, themeOverride } from 'ui-components';
import { HeapInitializer } from '~/components/HeapInitializer';
import { queryClient } from '~/config/reactQueryConfig';
import { IntercomProvider } from '~/contexts/Intercom';
import { MetricsProvider } from '~/contexts/Metrics';
import { AuthProvider } from '~/contexts/auth';
import { useDatadogSetUser } from '~/hooks/useDatadogSetUser';
import { useSentrySetUser } from '~/hooks/useSentrySetUser';
import store from '~/store';
import customTheme from '~/styles/theme';

import { FontOverride } from '../FontOverride/FontOverride';
import GlobalErrorBoundary from '../GlobalErrorBoundary';
import { MaintenanceModeInterceptor } from '../MaintenanceModeInterceptor';

const DevTools = dynamic(() =>
  import('@tanstack/react-query-devtools').then(
    ({ ReactQueryDevtools }) => ReactQueryDevtools,
  ),
);

// Avoids rendering the Toaster on the server side to avoid hydration errors
const Toaster = dynamic(
  () =>
    import('@foam/buoy/src/components/Toast/Toast').then((mod) => mod.Toaster),
  {
    ssr: false,
  },
);

function ReduxInnerProviders({
  children,
  isSSR,
  dehydratedState,
  isSupportedBrowser,
}: {
  children: React.ReactNode;
  dehydratedState: DehydratedState;
  isSSR: boolean;
  isSupportedBrowser: boolean;
}) {
  // Initialize user info in Sentry and Datadog
  useSentrySetUser();
  useDatadogSetUser();

  return (
    <ThemeProvider
      fonts={['400', '500', 'mono-400']}
      theme={{
        ...customTheme,
        components: {
          ...customTheme.components,
          DatePicker: {
            baseStyle: {
              calendar: {
                zIndex: customTheme.zIndices.modal + 1,
              },
            },
          },
          Select: {
            ...themeOverride.components.Select,
          },
          FloatingBottomMultiselectActions: {
            baseStyle: {
              floatingBar: {
                zIndex: 99,
              },
            },
          },
        },
      }}
    >
      <GlobalErrorBoundary isSupportedBrowser={isSupportedBrowser}>
        <QueryClientProvider client={queryClient}>
          <Hydrate state={dehydratedState}>
            {process.env.NODE_ENV !== 'production' && (
              <DevTools
                initialIsOpen={false}
                toggleButtonProps={{ style: { right: 'unset' } }}
              />
            )}
            <MetricsProvider>
              <IntercomProvider>
                <AuthProvider skipLoading={isSSR}>
                  <MaintenanceModeInterceptor>
                    {children}
                    <FontOverride />
                    <Toaster />
                  </MaintenanceModeInterceptor>
                </AuthProvider>
              </IntercomProvider>
            </MetricsProvider>
            <HeapInitializer />
          </Hydrate>
        </QueryClientProvider>
      </GlobalErrorBoundary>
    </ThemeProvider>
  );
}

export function AppProviders({
  children,
  dehydratedState,
  isSSR,
  isSupportedBrowser,
}: {
  children: React.ReactNode;
  dehydratedState: DehydratedState;
  isSSR: boolean;
  isSupportedBrowser: boolean;
}) {
  return (
    <ReduxProvider store={store}>
      <ReduxInnerProviders
        isSSR={isSSR}
        dehydratedState={dehydratedState}
        isSupportedBrowser={isSupportedBrowser}
      >
        {children}
      </ReduxInnerProviders>
    </ReduxProvider>
  );
}
